const BASE = '/api';

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(path, { method = 'GET', body, isForm = false } = {}) {
  const init = { method, credentials: 'same-origin', headers: {} };

  if (body !== undefined) {
    if (isForm) {
      init.body = body; // FormData si Content-Type nastaví sám i s hranicí
    } else {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
  }

  let response;
  try {
    response = await fetch(BASE + path, init);
  } catch {
    throw new ApiError('Nepodařilo se spojit se serverem.', 0);
  }

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApiError(payload?.error || `Chyba serveru (${response.status}).`, response.status);
  }
  return payload;
}

/**
 * Nahrávání jde přes XMLHttpRequest, protože fetch neumí říct, kolik už je
 * odesláno — a u záznamu, který má gigabajt, je ukazatel průběhu nutnost.
 */
function uploadFiles(formData, onProgress) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `${BASE}/admin/files`);

    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    });

    request.addEventListener('load', () => {
      let payload = null;
      try {
        payload = JSON.parse(request.responseText);
      } catch {
        /* server neodpověděl JSONem */
      }
      if (request.status >= 200 && request.status < 300) resolve(payload);
      else reject(new ApiError(payload?.error || `Chyba serveru (${request.status}).`, request.status));
    });

    request.addEventListener('error', () => reject(new ApiError('Spojení se serverem se přerušilo.', 0)));
    request.addEventListener('abort', () => reject(new ApiError('Nahrávání bylo zrušeno.', 0)));

    request.send(formData);
  });
}

export const api = {
  content: () => request('/content'),

  auth: {
    me: () => request('/auth/me'),
    login: (username, password) => request('/auth/login', { method: 'POST', body: { username, password } }),
    logout: () => request('/auth/logout', { method: 'POST' }),
    changePassword: (currentPassword, newPassword) =>
      request('/auth/password', { method: 'POST', body: { currentPassword, newPassword } }),
  },

  admin: {
    content: () => request('/admin/content'),

    createYear: (data) => request('/admin/years', { method: 'POST', body: data }),
    updateYear: (id, data) => request(`/admin/years/${id}`, { method: 'PATCH', body: data }),
    deleteYear: (id) => request(`/admin/years/${id}`, { method: 'DELETE' }),
    setCurrentYear: (id) => request(`/admin/years/${id}/current`, { method: 'POST' }),

    createLecture: (data) => request('/admin/lectures', { method: 'POST', body: data }),
    updateLecture: (id, data) => request(`/admin/lectures/${id}`, { method: 'PATCH', body: data }),
    deleteLecture: (id) => request(`/admin/lectures/${id}`, { method: 'DELETE' }),

    createAssignment: (data) => request('/admin/assignments', { method: 'POST', body: data }),
    updateAssignment: (id, data) => request(`/admin/assignments/${id}`, { method: 'PATCH', body: data }),
    deleteAssignment: (id) => request(`/admin/assignments/${id}`, { method: 'DELETE' }),

    createMaterial: (data) => request('/admin/materials', { method: 'POST', body: data }),
    updateMaterial: (id, data) => request(`/admin/materials/${id}`, { method: 'PATCH', body: data }),
    deleteMaterial: (id) => request(`/admin/materials/${id}`, { method: 'DELETE' }),

    reorder: (kind, ids) => request(`/admin/reorder/${kind}`, { method: 'POST', body: { ids } }),

    files: () => request('/admin/files'),
    fileLimits: () => request('/admin/files/limits'),
    upload: (formData, onProgress) => uploadFiles(formData, onProgress),
    convertFile: (id) => request(`/admin/files/${id}/convert`, { method: 'POST' }),
    deleteFile: (id) => request(`/admin/files/${id}`, { method: 'DELETE' }),
  },
};

export { ApiError };
